import { lazy, Suspense, type LazyExoticComponent, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { PageLoader } from '@/components/ui/PageLoader';
import { ForceLocale } from '@/components/seo/ForceLocale';

/**
 * Every page is a separate chunk, fetched only when its route is actually
 * visited -- someone reading about thobe cleaning shouldn't download the
 * checkout flow, the admin panel, or the driver app. `L()` pairs a lazy
 * import with the one Suspense fallback every route uses, so a route
 * definition below stays a one-liner instead of repeating
 * `<Suspense fallback={...}><X /></Suspense>` sixty times.
 *
 * The optional `forceLocale` is only ever passed for a /ar/* mirror route
 * (below) -- it guarantees the page is genuinely in Arabic the instant a
 * crawler lands on it, independent of whatever the header toggle last set.
 */
function L(Component: LazyExoticComponent<ComponentType>, forceLocale?: 'ar') {
  const el = (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
  return forceLocale ? <ForceLocale lang={forceLocale}>{el}</ForceLocale> : el;
}

const HomePage         = lazy(() => import('@/pages/marketing/HomePage'));
const AboutPage        = lazy(() => import('@/pages/marketing/AboutPage'));
const ServicesPage     = lazy(() => import('@/pages/marketing/ServicesPage'));
const WhyUsPage        = lazy(() => import('@/pages/marketing/WhyUsPage'));
const ContactPage      = lazy(() => import('@/pages/marketing/ContactPage'));
const OrderPage        = lazy(() => import('@/pages/order/OrderPage'));
const CartPage         = lazy(() => import('@/pages/order/CartPage'));
const AddressStep      = lazy(() => import('@/pages/checkout/AddressStep'));
const SlotStep         = lazy(() => import('@/pages/checkout/SlotStep'));
const ReviewStep       = lazy(() => import('@/pages/checkout/ReviewStep'));
const ConfirmationPage = lazy(() => import('@/pages/order/ConfirmationPage'));
const LoginPage        = lazy(() => import('@/pages/auth/LoginPage'));
const AccountPage      = lazy(() => import('@/pages/auth/AccountPage'));
const CustomerCoupons  = lazy(() => import('@/pages/account/CustomerCoupons'));
const ProfilePage      = lazy(() => import('@/pages/account/ProfilePage'));
const ReferralsPage    = lazy(() => import('@/pages/account/ReferralsPage'));
const ReferralLandingPage = lazy(() => import('@/pages/marketing/ReferralLandingPage'));
const PrivacyPage       = lazy(() => import('@/pages/marketing/PrivacyPage'));
const NotFoundPage     = lazy(() => import('@/pages/NotFoundPage'));
const QuoteApprovalPage = lazy(() => import('@/pages/order/QuoteApprovalPage'));
const ReviewPage = lazy(() => import('@/pages/order/ReviewPage'));

import { AdminGuard }   from '@/components/admin/AdminGuard';
import { AdminShell }   from '@/components/admin/AdminShell';
const AdminDashboard   = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminItems       = lazy(() => import('@/pages/admin/AdminItems'));
const AdminItemEdit    = lazy(() => import('@/pages/admin/AdminItemEdit'));
const AdminCategories  = lazy(() => import('@/pages/admin/AdminCategories'));
const AdminCategoryEdit = lazy(() => import('@/pages/admin/AdminCategoryEdit'));
const AdminServices    = lazy(() => import('@/pages/admin/AdminServices'));
const AdminServiceEdit = lazy(() => import('@/pages/admin/AdminServiceEdit'));
const AdminBundles     = lazy(() => import('@/pages/admin/AdminBundles'));
const AdminBundleEdit  = lazy(() => import('@/pages/admin/AdminBundleEdit'));
const AdminOrders      = lazy(() => import('@/pages/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('@/pages/admin/AdminOrderDetail'));
const AdminQuotes      = lazy(() => import('@/pages/admin/AdminQuotes'));
const AdminDrivers     = lazy(() => import('@/pages/admin/AdminDrivers'));
const AdminSupervisors = lazy(() => import('@/pages/admin/AdminSupervisors'));
const AdminSupervisorSchedule = lazy(() => import('@/pages/admin/AdminSupervisorSchedule'));
const AdminDriverEdit  = lazy(() => import('@/pages/admin/AdminDriverEdit'));
const AdminDispatchBoard = lazy(() => import('@/pages/admin/AdminDispatchBoard'));
const AdminCashFloats  = lazy(() => import('@/pages/admin/AdminCashFloats'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettings'));
const AdminCoupons     = lazy(() => import('@/pages/admin/AdminCoupons'));
const AdminCouponNew   = lazy(() => import('@/pages/admin/AdminCouponNew'));
const AdminCouponBatch = lazy(() => import('@/pages/admin/AdminCouponBatch'));
const AdminCouponDetail = lazy(() => import('@/pages/admin/AdminCouponDetail'));
const AdminFlyers      = lazy(() => import('@/pages/admin/AdminFlyers'));
const AdminFlyerNew    = lazy(() => import('@/pages/admin/AdminFlyerNew'));
const AdminFlyerDetail = lazy(() => import('@/pages/admin/AdminFlyerDetail'));
const AdminCustomers   = lazy(() => import('@/pages/admin/AdminCustomers'));
const AdminCustomerDetail = lazy(() => import('@/pages/admin/AdminCustomerDetail'));
const AdminSocialClaims = lazy(() => import('@/pages/admin/AdminSocialClaims'));
const AdminHandovers = lazy(() => import('@/pages/admin/AdminHandovers'));
const AdminReports = lazy(() => import('@/pages/admin/AdminReports'));
const AdminHelp = lazy(() => import('@/pages/admin/AdminHelp'));
const AdminReviews = lazy(() => import('@/pages/admin/AdminReviews'));

import { DriverGuard }  from '@/components/driver/DriverGuard';
import { DriverShell }  from '@/components/driver/DriverShell';
const DriverJobs       = lazy(() => import('@/pages/driver/DriverJobs'));
const DriverJobDetail  = lazy(() => import('@/pages/driver/DriverJobDetail'));
const DriverCount      = lazy(() => import('@/pages/driver/DriverCount'));
const DriverRefuse     = lazy(() => import('@/pages/driver/DriverRefuse'));
const DriverDeliver    = lazy(() => import('@/pages/driver/DriverDeliver'));
const DriverShift      = lazy(() => import('@/pages/driver/DriverShift'));
const DriverHandovers       = lazy(() => import('@/pages/driver/DriverHandovers'));
const DriverHandoverDetail  = lazy(() => import('@/pages/driver/DriverHandoverDetail'));

import { SupervisorGuard } from '@/components/supervisor/SupervisorGuard';
import { SupervisorShell } from '@/components/supervisor/SupervisorShell';
const SupervisorQueue       = lazy(() => import('@/pages/supervisor/SupervisorQueue'));
const SupervisorOrderDetail = lazy(() => import('@/pages/supervisor/SupervisorOrderDetail'));
const SupervisorInspections = lazy(() => import('@/pages/supervisor/SupervisorInspections'));
const SupervisorHandovers       = lazy(() => import('@/pages/supervisor/SupervisorHandovers'));
const SupervisorHandoverConfirm = lazy(() => import('@/pages/supervisor/SupervisorHandoverConfirm'));

export const router = createBrowserRouter([
  // Marketing-shell routes (Header + Footer)
  {
    element: <MarketingLayout />,
    children: [
      { path: '/',         element: L(HomePage) },
      { path: '/about',    element: L(AboutPage) },
      { path: '/services', element: L(ServicesPage) },
      { path: '/why-us',   element: L(WhyUsPage) },
      { path: '/pricing',  element: <Navigate to="/order" replace /> },
      { path: '/book',     element: <Navigate to="/order" replace /> },
      { path: '/order',    element: L(OrderPage) },
      { path: '/cart',     element: L(CartPage) },

      { path: '/checkout',         element: <Navigate to="/checkout/address" replace /> },
      { path: '/checkout/address', element: L(AddressStep) },
      { path: '/checkout/slot',    element: L(SlotStep) },
      { path: '/checkout/review',  element: L(ReviewStep) },

      { path: '/order/confirmation/:order_no', element: L(ConfirmationPage) },
      { path: '/order/:order_no/quotes',       element: L(QuoteApprovalPage) },
      { path: '/reviews/:id',                  element: L(ReviewPage) },

      { path: '/contact',  element: L(ContactPage) },
      { path: '/privacy',  element: L(PrivacyPage) },
      { path: '/account',  element: L(AccountPage) },
      { path: '/account/coupons', element: L(CustomerCoupons) },
      { path: '/account/profile', element: L(ProfilePage) },
      { path: '/account/referrals', element: L(ReferralsPage) },
      { path: '/r/:code', element: L(ReferralLandingPage) },

      // Arabic mirrors (Phase 20 -- hreflang) of the real content pages
      // that get one: components/seo/Hreflang.tsx pairs each of these
      // with its bare English equivalent above. Not every marketing route
      // needs a mirror -- only the ones that are genuinely static,
      // indexable content, not an interactive flow (checkout, account,
      // /r/:code) or a page that's currently just a redirect (/pricing).
      { path: '/ar',         element: L(HomePage, 'ar') },
      { path: '/ar/about',   element: L(AboutPage, 'ar') },
      { path: '/ar/services', element: L(ServicesPage, 'ar') },
      { path: '/ar/why-us',  element: L(WhyUsPage, 'ar') },
      { path: '/ar/contact', element: L(ContactPage, 'ar') },
    ],
  },

  // Admin shell — admin-only, no marketing chrome
  {
    element: <AdminGuard><AdminShell /></AdminGuard>,
    children: [
      { path: '/admin',                element: L(AdminDashboard) },
      { path: '/admin/items',          element: L(AdminItems) },
      { path: '/admin/items/new',      element: L(AdminItemEdit) },
      { path: '/admin/items/:code',    element: L(AdminItemEdit) },
      { path: '/admin/categories',     element: L(AdminCategories) },
      { path: '/admin/categories/new',   element: L(AdminCategoryEdit) },
      { path: '/admin/categories/:code', element: L(AdminCategoryEdit) },
      { path: '/admin/services',       element: L(AdminServices) },
      { path: '/admin/services/new',    element: L(AdminServiceEdit) },
      { path: '/admin/services/:code',  element: L(AdminServiceEdit) },
      { path: '/admin/bundles',         element: L(AdminBundles) },
      { path: '/admin/bundles/new',     element: L(AdminBundleEdit) },
      { path: '/admin/bundles/:code',   element: L(AdminBundleEdit) },

      // Operations — Phase 10d
      { path: '/admin/orders',            element: L(AdminOrders) },
      { path: '/admin/orders/:order_no',  element: L(AdminOrderDetail) },
      { path: '/admin/quotes',            element: L(AdminQuotes) },

      // Phase 10e
      { path: '/admin/drivers',      element: L(AdminDrivers) },
      { path: '/admin/drivers/new',  element: L(AdminDriverEdit) },
      { path: '/admin/drivers/:id',  element: L(AdminDriverEdit) },
      { path: '/admin/supervisors',           element: L(AdminSupervisors) },
      { path: '/admin/supervisors/schedule',  element: L(AdminSupervisorSchedule) },
      { path: '/admin/handovers',             element: L(AdminHandovers) },
      { path: '/admin/reports',               element: L(AdminReports) },
      { path: '/admin/reviews',               element: L(AdminReviews) },
      { path: '/admin/help',                  element: L(AdminHelp) },
      { path: '/admin/help/:slug',            element: L(AdminHelp) },
      { path: '/admin/dispatch',     element: L(AdminDispatchBoard) },
      { path: '/admin/cashfloats',   element: L(AdminCashFloats) },
      { path: '/admin/settings',     element: L(AdminSettingsPage) },

      // Phase 13 Commit 2
      { path: '/admin/coupons',        element: L(AdminCoupons) },
      { path: '/admin/coupons/new',    element: L(AdminCouponNew) },
      { path: '/admin/coupons/batch',  element: L(AdminCouponBatch) },
      { path: '/admin/coupons/:id',    element: L(AdminCouponDetail) },

      // Phase 13 Commit 4
      { path: '/admin/flyers',        element: L(AdminFlyers) },
      { path: '/admin/flyers/new',    element: L(AdminFlyerNew) },
      { path: '/admin/flyers/:id',    element: L(AdminFlyerDetail) },

      // Phase 16 Commit 2
      { path: '/admin/customers',      element: L(AdminCustomers) },
      { path: '/admin/customers/:id',  element: L(AdminCustomerDetail) },

      // Phase 16 Commit 3
      { path: '/admin/social-claims',  element: L(AdminSocialClaims) },
    ],
  },

  // Driver shell — Phase 11. Its own shell, not the admin one: mobile-first,
  // large tap targets, used one-handed standing in a stairwell.
  {
    element: <DriverGuard><DriverShell /></DriverGuard>,
    children: [
      { path: '/driver',                      element: L(DriverJobs) },
      { path: '/driver/jobs/:order_no',        element: L(DriverJobDetail) },
      { path: '/driver/jobs/:order_no/count',  element: L(DriverCount) },
      { path: '/driver/jobs/:order_no/refuse', element: L(DriverRefuse) },
      { path: '/driver/jobs/:order_no/deliver', element: L(DriverDeliver) },
      { path: '/driver/shift',                element: L(DriverShift) },
      { path: '/driver/handovers',            element: L(DriverHandovers) },
      { path: '/driver/handovers/:id',        element: L(DriverHandoverDetail) },
    ],
  },

  // Supervisor shell — Phase 11 Commit 3. Facility desk/tablet surface,
  // guarded on orders.facility.edit specifically, not the admin shell.
  {
    element: <SupervisorGuard><SupervisorShell /></SupervisorGuard>,
    children: [
      { path: '/supervisor',                      element: L(SupervisorQueue) },
      { path: '/supervisor/orders/:order_no',      element: L(SupervisorOrderDetail) },
      { path: '/supervisor/inspections',           element: L(SupervisorInspections) },
      { path: '/supervisor/handovers',             element: L(SupervisorHandovers) },
      { path: '/supervisor/handovers/:code',       element: L(SupervisorHandoverConfirm) },
    ],
  },

  // Standalone-shell routes (no chrome)
  { path: '/login', element: L(LoginPage) },

  // 404
  {
    element: <MarketingLayout />,
    children: [{ path: '*', element: L(NotFoundPage) }],
  },
]);
